import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { GlobalMessageActions } from '../../../../../core/global-message/store';
import { MultipleArticlesAddedResponse } from '../../../../../core/model';
import { CartModification } from '../../../../../core/model/cart.model';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { AsyncReturnService } from '../../../../../shared';
import { CheckoutActions } from '../../../../checkout/base/store/actions';
import { CartEntryConnector } from '../../connector/cart-entry.connector';
import { CartEntryActionTypes } from '../actions/cart-entry.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  addEntry$: Observable<CartActions.AddCartEntrySuccess | CartActions.AddCartEntryFail | CartActions.LoadCart> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.AddCartEntry),
        map((action: CartActions.AddCartEntry) => action.payload),
        concatMap(({ actionToken, ...payload }) => {
          return this.connector
            .add(
              payload.userId,
              payload.cartId,
              payload.articleCode,
              payload.quantity,
              payload.unit,
              payload.orderCardCode,
              payload.customerMaterialNumber,
              payload.cartTemplate,
              payload.itemListType
            )
            .pipe(
              map((cartModification: CartModification) => {
                this.asyncReturnService.resolveToken(actionToken, true);
                return new CartActions.AddCartEntrySuccess({
                  ...payload,
                  ...(cartModification as Required<CartModification>),
                });
              }),
              catchError((error) => {
                this.asyncReturnService.resolveToken(actionToken, false);
                return from([
                  new CartActions.AddCartEntryFail({
                    ...payload,
                    error: normalizeHttpError(error),
                  }),
                  new CartActions.LoadCart({
                    cartId: payload.cartId,
                    cartType: payload.cartType,
                    userId: payload.userId,
                  }),
                ]);
              })
            );
        }),
        withdrawOn(this.contextChange$)
      )
  );

  handleFailureForAddEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.AddCartEntryFail),
      map((action: CartActions.AddCartEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.addCartEntryFailure_message',
              errorDetails: action.payload.error.details.map((detail) => ({
                message: detail.message,
                subject: action.payload.articleCode,
                translationKey:
                  detail.type === 'MixedNonAndPrePrintedLabelError' ? 'common.error.mixednonandpreprintedlabel_message' : '',
              })),
              params: {
                articleNumber: action.payload.articleCode,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  addA4SampleEntry$: Observable<
    CartActions.AddA4SampleCartEntrySuccess | CartActions.AddA4SampleCartEntryFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.AddA4SampleCartEntry),
      map((action: CartActions.AddA4SampleCartEntry) => action.payload),
      concatMap(({ actionToken, ...payload }) => {
        return this.connector
          .addA4Sample(payload.userId, payload.cartId, payload.productCode, payload.grammage, payload.color, payload.quantity)
          .pipe(
            map((cartModification: CartModification) => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return new CartActions.AddA4SampleCartEntrySuccess({
                ...payload,
                ...(cartModification as Required<CartModification>),
              });
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.AddA4SampleCartEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  cartType: payload.cartType,
                  userId: payload.userId,
                }),
              ]);
            })
          );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForAddA4SampleEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.AddA4SampleCartEntryFail),
      map((action: CartActions.AddA4SampleCartEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.addCartEntryA4SampleFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  quickAddEntry$: Observable<CartActions.QuickAddCartEntrySuccess | CartActions.QuickAddCartEntryFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.QuickAddCartEntry),
        map((action: CartActions.QuickAddCartEntry) => action.payload),
        concatMap(({ actionToken, ...payload }) => {
          return this.connector
            .quickAdd(
              payload.userId,
              payload.cartId,
              payload.articleCode,
              payload.quantity,
              payload.unit,
              payload.note,
              payload.customerMaterialNumber,
              payload.itemListType
            )
            .pipe(
              map((cartModification: CartModification) => {
                this.asyncReturnService.resolveToken(actionToken, true);
                return new CartActions.QuickAddCartEntrySuccess({
                  ...payload,
                  ...(cartModification as Required<CartModification>),
                });
              }),
              catchError((error) => {
                this.asyncReturnService.resolveToken(actionToken, false);
                return from([
                  new CartActions.QuickAddCartEntryFail({
                    ...payload,
                    error: normalizeHttpError(error),
                  }),
                  new CartActions.LoadCart({
                    cartId: payload.cartId,
                    cartType: payload.cartType,
                    userId: payload.userId,
                  }),
                ]);
              })
            );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForQuickAddEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.QuickAddCartEntryFail),
      map((action: CartActions.QuickAddCartEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.quickAddCartEntryFailure_message',
              params: {
                articleNumber: action.payload.articleCode,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  addEntries$: Observable<CartActions.AddCartEntriesSuccess | CartActions.AddCartEntriesFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.AddCartEntries),
        map((action: CartActions.AddCartEntries) => action.payload),
        concatMap(({ actionToken, ...payload }) => {
          return this.connector
            .addMultiple(payload.userId, payload.cartId, payload.entries, payload.sourceData, payload.itemListType)
            .pipe(
              map((response: MultipleArticlesAddedResponse) => {
                this.asyncReturnService.resolveToken(actionToken, true);
                return new CartActions.AddCartEntriesSuccess({
                  ...payload,
                  ...(response as Required<MultipleArticlesAddedResponse>),
                });
              }),
              catchError((error) => {
                this.asyncReturnService.resolveToken(actionToken, false);
                return from([
                  new CartActions.AddCartEntriesFail({
                    ...payload,
                    error: normalizeHttpError(error),
                  }),
                  new CartActions.LoadCart({
                    cartId: payload.cartId,
                    cartType: payload.cartType,
                    userId: payload.userId,
                  }),
                ]);
              })
            );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForAddEntries$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.AddCartEntriesFail),
      map((action: CartActions.AddCartEntriesFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.addCartEntriesFailure_message',
              details: action.payload.error.details?.map((detail) => detail.message),
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeEntry$: Observable<CartActions.RemoveCartEntrySuccess | CartActions.RemoveCartEntryFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.RemoveCartEntry),
        map((action: CartActions.RemoveCartEntry) => action.payload),
        concatMap(({ actionToken, ...payload }) =>
          this.connector.remove(payload.userId, payload.cartId, payload.entry).pipe(
            map((cartModification) => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return new CartActions.RemoveCartEntrySuccess({
                ...payload,
                cartModification,
              });
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.RemoveCartEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  userId: payload.userId,
                  cartType: payload.cartType,
                  cartId: payload.cartId,
                }),
              ]);
            })
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForRemoveEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.RemoveCartEntryFail),
      map((action: CartActions.RemoveCartEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeEntries$: Observable<CartActions.RemoveCartEntriesSuccess | CartActions.RemoveCartEntriesFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.RemoveCartEntries),
        map((action: CartActions.RemoveCartEntries) => action.payload),
        concatMap(({ actionToken, ...payload }) =>
          this.connector.removeMultiple(payload.userId, payload.cartId, payload.entries).pipe(
            map((cartModifications) => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return new CartActions.RemoveCartEntriesSuccess({
                ...payload,
                cartModifications,
              });
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.RemoveCartEntriesFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  userId: payload.userId,
                  cartType: payload.cartType,
                  cartId: payload.cartId,
                }),
              ]);
            })
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForRemoveEntries$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.RemoveCartEntriesFail),
      map((action: CartActions.RemoveCartEntriesFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryFailure_message',
              params: {
                entryNumbers: action.payload.entries.map(({ entryNumber }) => +entryNumber + 1).join(', '),
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeAllEntries$: Observable<
    CartActions.RemoveAllCartEntriesSuccess | CartActions.RemoveAllCartEntriesFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.RemoveAllCartEntries),
      map((action: CartActions.RemoveAllCartEntries) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.removeAll(payload.userId, payload.cartId).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.RemoveAllCartEntriesSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.RemoveAllCartEntriesFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateEntry$: Observable<CartActions.UpdateCartEntrySuccess | CartActions.UpdateCartEntryFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.UpdateCartEntry),
        map((action: CartActions.UpdateCartEntry) => action.payload),
        concatMap(({ actionToken, ...payload }) =>
          this.connector.update(payload.userId, payload.cartId, payload.entry, payload.quantity, payload.unit).pipe(
            map((cartModification) => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return new CartActions.UpdateCartEntrySuccess({
                ...payload,
                cartModification,
              });
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.UpdateCartEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  userId: payload.userId,
                  cartType: payload.cartType,
                  cartId: payload.cartId,
                }),
              ]);
            })
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  setOrderReviewed$: Observable<CheckoutActions.SetOrderReviewed> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntrySuccess),
      map((action: CartActions.UpdateCartEntrySuccess) => {
        return new CheckoutActions.SetOrderReviewed({ key: action.payload.cartType, isReviewed: false });
      }, withdrawOn(this.contextChange$))
    )
  );

  updateEntryNote$: Observable<
    | CartActions.LoadCart
    | CartActions.UpdateCartEntryFail
    | CartActions.UpdateCartEntrySuccess
    | CartActions.UpdateCartEntryNoteSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryNote),
      map((action: CartActions.UpdateCartEntryNote) => action.payload),
      switchMap(({ actionToken, ...payload }) =>
        this.connector.updateNote(payload.note, payload.userId, payload.cartId, payload.entry).pipe(
          mergeMap((cartModification) => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return [
              new CartActions.UpdateCartEntrySuccess({
                ...payload,
                cartModification,
              }),
              new CartActions.UpdateCartEntryNoteSuccess(payload.cartId),
            ];
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.UpdateCartEntryFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateEntryNoteSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryNoteSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.updateEntryNoteSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  updateEntryNameMarking$: Observable<
    | CartActions.LoadCart
    | CartActions.UpdateCartEntryFail
    | CartActions.UpdateCartEntrySuccess
    | CartActions.UpdateCartEntryNameMarkingSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryNameMarking),
      map((action: CartActions.UpdateCartEntryNameMarking) => action.payload),
      switchMap(({ actionToken, ...payload }) =>
        this.connector
          .updateNameMarking(
            payload.userId,
            payload.cartId,
            payload.entry,
            payload.qualifier,
            payload.ruleId,
            payload.extendedIdentifier,
            payload.values
          )
          .pipe(
            mergeMap((cartModification) => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return [
                new CartActions.UpdateCartEntrySuccess({
                  userId: payload.userId,
                  cartId: payload.cartId,
                  cartType: payload.cartType,
                  entry: payload.entry,
                  cartModification,
                }),
                new CartActions.UpdateCartEntryNameMarkingSuccess(payload.cartId),
              ];
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.UpdateCartEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  userId: payload.userId,
                  cartType: payload.cartType,
                  cartId: payload.cartId,
                }),
              ]);
            })
          )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateEntryNameMarkingSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryNameMarkingSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.updateEntryNameMarkingSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  updateEntryStatisticsCode$: Observable<
    | CartActions.LoadCart
    | CartActions.UpdateCartEntryFail
    | CartActions.UpdateCartEntrySuccess
    | CartActions.UpdateCartEntryStatisticsCodeSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryStatisticsCode),
      map((action: CartActions.UpdateCartEntryStatisticsCode) => action.payload),
      switchMap(({ actionToken, ...payload }) =>
        this.connector.updateStatisticsCode(payload.statisticsCode, payload.userId, payload.cartId, payload.entry).pipe(
          mergeMap((cartModification) => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return [
              new CartActions.UpdateCartEntrySuccess({
                ...payload,
                cartModification,
              }),
              new CartActions.UpdateCartEntryStatisticsCodeSuccess(payload.cartId),
            ];
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.UpdateCartEntryFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateEntryStatisticsCodeSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryStatisticsCodeSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.updateEntryStatisticsCodeSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  updateEntryAccountingCode$: Observable<
    | CartActions.LoadCart
    | CartActions.UpdateCartEntryFail
    | CartActions.UpdateCartEntrySuccess
    | CartActions.UpdateCartEntryAccountingCodeSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryAccountingCode),
      map((action: CartActions.UpdateCartEntryAccountingCode) => action.payload),
      switchMap(({ actionToken, ...payload }) =>
        this.connector.updateAccountingCode(payload.accountingCode, payload.userId, payload.cartId, payload.entry).pipe(
          mergeMap((cartModification) => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return [
              new CartActions.UpdateCartEntrySuccess({
                ...payload,
                cartModification,
              }),
              new CartActions.UpdateCartEntryAccountingCodeSuccess(payload.cartId),
            ];
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.UpdateCartEntryFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateEntryAccountingCodeSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryAccountingCodeSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.updateEntryAccountingCodeSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  handleFailureForUpdateEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.UpdateCartEntryFail),
      map((action: CartActions.UpdateCartEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.updateCartEntryFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  replaceEntry$: Observable<CartActions.ReplaceCartEntrySuccess | CartActions.ReplaceCartEntryFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryActionTypes.ReplaceCartEntry),
        map((action: CartActions.ReplaceCartEntry) => action.payload),
        concatMap(({ actionToken, ...payload }) =>
          this.connector
            .replace(payload.userId, payload.cartId, payload.entry, payload.articleNumber, payload.quantity, payload.unit)
            .pipe(
              map((cartModification) => {
                this.asyncReturnService.resolveToken(actionToken, true);
                return new CartActions.ReplaceCartEntrySuccess({
                  ...payload,
                  cartModification,
                });
              }),
              catchError((error) => {
                this.asyncReturnService.resolveToken(actionToken, false);
                return from([
                  new CartActions.ReplaceCartEntryFail({
                    ...payload,
                    error: normalizeHttpError(error),
                  }),
                  new CartActions.LoadCart({
                    userId: payload.userId,
                    cartType: payload.cartType,
                    cartId: payload.cartId,
                  }),
                ]);
              })
            )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleSuccessForReplaceEntry$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryActionTypes.ReplaceCartEntrySuccess),
      map((action: CartActions.ReplaceCartEntrySuccess) => action.payload),
      map((payload) => {
        return new GlobalMessageActions.AddMessage({
          text: {
            key: 'cart.replaceEntrySuccess_message',
            uid: `${payload.cartId}-${payload.cartModification?.entry?.articleRef}`,
          },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        });
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartEntryConnector,
    private asyncReturnService: AsyncReturnService
  ) {}
}
